import { useAtomValue } from "jotai";
import { type FunctionComponent, useCallback } from "react";

import { useTrackImpressionEventByIntersection } from "shared/hooks";

import type { BlurbCardHighlightSection } from "scmp-app/components/plus/atoms";
import { plusPopupAtom } from "scmp-app/components/plus/atoms";
import { type Props as PlusItemCardProps } from "scmp-app/components/plus/blurb-popup/card";
import { PlusItemCard } from "scmp-app/components/plus/blurb-popup/card";
import { usePlusBlurbPopup } from "scmp-app/components/plus/blurb-popup/hooks";
import { PlusBlurbMessage } from "scmp-app/components/plus/blurb-popup/message";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { PlusNonSubBlurbImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";

import {
  Backdrop,
  CardItemWrapper,
  Container,
  PlusBlurbMessageWrapper,
  StyledModal,
  StyledSvgDailyPulseLogo,
  StyledSvgNewsAgendaLogo,
  StyledSvgScmpPlusHighlightsLogo,
} from "./styles";

const cardItems: (PlusItemCardProps & { section: BlurbCardHighlightSection })[] = [
  {
    description:
      "Concise insights, analysis and context on a key China-related issue plus a round-up of how it’s been reported across the media.",
    icon: <StyledSvgDailyPulseLogo />,
    section: "daily-pulse",
    title: "daily pulse",
  },
  {
    description:
      "Comprehensive calendar of China-centric events, including political, policy, economic, diplomatic and business gatherings and milestones, plus related articles and fact sheets",
    icon: <StyledSvgNewsAgendaLogo />,
    section: "news-agenda",
    title: "news agenda",
  },
  {
    description:
      "Early access to views from thought leaders and columnists as well as infographics and fact sheets developed and curated by the SCMP Plus editorial team.",
    icon: <StyledSvgScmpPlusHighlightsLogo />,
    section: "scmp-plus-highlights",
    title: "highlights from scmp plus",
  },
];

export const PlusBlurbPopup: FunctionComponent = () => {
  const { currentBlurbCardHighlightSection, isShowBlurbPopup } = useAtomValue(plusPopupAtom);
  const { handleClose, isSubscriber } = usePlusBlurbPopup();

  const impressionGA4Event = useCallback<() => PlusNonSubBlurbImpressionEvent>(
    () => ({
      action: "imp",
      category: "subs",
      customized_parameters: {
        call_to_action: isSubscriber ? "upgrade" : "plus trial",
        trigger_point: "non sub plus homepage paywall popup",
      },
      subcategory: "entry_point",
    }),
    [isSubscriber],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: impressionGA4Event,
    options: { isSendGA4Tracking: true, shouldSendOnce: true },
  });

  const ctaButtonOnClickEvent = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        call_to_action: isSubscriber ? "upgrade" : "plus trial",
        trigger_point: "non sub plus homepage paywall popup",
      },
      subcategory: "entry_point",
    });
  }, [isSubscriber]);

  return (
    <StyledModal BackdropComponent={Backdrop} onClose={handleClose} open={isShowBlurbPopup}>
      <Container>
        <CardItemWrapper>
          {cardItems.map((cardItem, index) => (
            <PlusItemCard
              key={index}
              {...cardItem}
              isBackgroundActive={cardItem.section === currentBlurbCardHighlightSection}
            />
          ))}
        </CardItemWrapper>
        <PlusBlurbMessageWrapper ref={captureTrackImpressionEventTargetElement}>
          <PlusBlurbMessage ctaButtonOnClick={ctaButtonOnClickEvent} />
        </PlusBlurbMessageWrapper>
      </Container>
    </StyledModal>
  );
};

PlusBlurbPopup.displayName = "PlusBlurbPopup";
